import * as React from 'react';

import styled from 'styled-components';

import { COLOR, FONT_SIZE, SPACING } from '../../packages/constants';
import { HelperModal, QuestionMark } from '../helper-modal';
import { PasswordInput } from '../password-input';

export interface PasswordFieldProps {
    dataId: string;
    label: string;
    id: string;
    name: string;
    autofocus?: boolean;
    defaultValue?: string;
    disabled?: boolean;
    invalid?: boolean;
    hasHelper?: boolean;
    helperContent?: React.ReactNode;
    numeralsOnly?: boolean;
    placeholder?: string;
    value?: string;
    onChange?: (e: any) => void;
    maxLength?: number;
    minLength?: number;
    pattern?: string;
    readOnly?: boolean;
    required?: boolean;
    thinLabel?: boolean;
    className?: string;
}

interface StyledTextLabelProps {
    disabled?: boolean;
    invalid?: boolean;
    thinLabel?: boolean;
}

const wrapperStyles = `
    display: flex;
    flex-direction: column;
`;

const Wrapper = styled.label`
    ${wrapperStyles}
`;

const WrapperHasHelper = styled.div`
    ${wrapperStyles}
`;

const StyledLabelWrapper = styled.div`
    display: inline-flex;
`;

const StyledTextLabel = styled.span<StyledTextLabelProps>`
    display: block;
    padding-bottom: ${SPACING.XX_SMALL};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledLabelHasHelper = styled.label<StyledTextLabelProps>`
    display: inline-block;
    margin-right: ${SPACING.X_SMALL};
    padding-bottom: ${SPACING.XX_SMALL};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

export const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
    (
        {
            dataId,
            label,
            id,
            hasHelper,
            helperContent,
            placeholder,
            name,
            autofocus,
            defaultValue,
            disabled,
            invalid,
            onChange,
            maxLength,
            minLength,
            pattern,
            readOnly,
            required,
            thinLabel,
            value,
            className,
            ...props
        },
        ref,
    ) => {
        return hasHelper ? (
            <WrapperHasHelper className={className} {...props}>
                <StyledLabelWrapper>
                    <StyledLabelHasHelper
                        data-test-id={`${dataId}-label`}
                        thinLabel={thinLabel}
                        invalid={invalid}
                        disabled={disabled}
                        htmlFor={id}
                    >
                        {label}
                    </StyledLabelHasHelper>
                    <HelperModal dataId={dataId} modalContent={helperContent}>
                        <QuestionMark />
                    </HelperModal>
                </StyledLabelWrapper>
                <PasswordInput
                    dataId={`${dataId}-input`}
                    id={id}
                    autoFocus={autofocus}
                    name={name}
                    placeholder={placeholder}
                    aria-label={id}
                    maxLength={maxLength}
                    minLength={minLength}
                    pattern={pattern}
                    readOnly={readOnly}
                    required={required}
                    invalid={invalid}
                    disabled={disabled}
                    {...(typeof value !== 'undefined' && { value })}
                    {...(typeof defaultValue !== 'undefined' && { defaultValue })}
                    ref={ref}
                />
            </WrapperHasHelper>
        ) : (
            <Wrapper data-test-id={`${dataId}-label`} className={className} {...props}>
                <StyledTextLabel invalid={invalid} disabled={disabled} thinLabel={thinLabel}>
                    {label}
                </StyledTextLabel>
                <PasswordInput
                    dataId={`${dataId}-input`}
                    id={id}
                    autoFocus={autofocus}
                    name={name}
                    placeholder={placeholder}
                    aria-label={id}
                    maxLength={maxLength}
                    minLength={minLength}
                    pattern={pattern}
                    readOnly={readOnly}
                    required={required}
                    invalid={invalid}
                    disabled={disabled}
                    {...(typeof value !== 'undefined' && { value })}
                    {...(typeof defaultValue !== 'undefined' && { defaultValue })}
                    onChange={onChange}
                    ref={ref}
                />
            </Wrapper>
        );
    },
);
PasswordField.displayName = 'PasswordField';
